<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { ROUTES } from '@/router/routes'
  import type { CustomerCase } from '@/types/generated'
  import { SavedResponsesService } from '@/types/generated/services.gen'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { ChevronLeft, FilePlus2, Loader2, Pencil, Trash2 } from 'lucide-vue-next'
  import { inject, ref, type Ref } from 'vue'

  const { t } = useTranslation({
    en: {
      Snippets: 'Snippets',
      Subtitle: 'Snippets are created from AI responses and can be used in the document generator.',
      NoSnippets: 'No snippets yet',
      GenerateDocument: 'Generate document',
      deleteError: 'Error deleting snippet'
    },
    de: {
      Snippets: 'Snippets',
      Subtitle:
        'Snippets werden aus KI-Antworten erstellt und können im Dokumentengenerator verwendet werden.',
      NoSnippets: 'Noch keine Snippets',
      GenerateDocument: 'Dokument generieren',
      deleteError: 'Fehler beim Löschen des Snippets'
    }
  })

  defineProps<{
    snippets: any[]
    snippetsLoading: boolean
  }>()

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))

  const emit = defineEmits<{
    back: []
    close: []
    refresh: []
    edit: [snippet: any]
  }>()

  const { toast } = useToast()

  const deletingUuid = ref<string | null>(null)

  const useSnippet = (snippet: any) => {
    window.dispatchEvent(
      new CustomEvent('use-saved-response', { detail: { content: snippet.content } })
    )
  }

  const deleteSnippet = async (snippet: any) => {
    deletingUuid.value = snippet.uuid
    try {
      await SavedResponsesService.savedResponsesDestroy({ uuid: snippet.uuid })
      emit('refresh')
    } catch {
      toast({ title: t('deleteError'), variant: 'destructive' })
    } finally {
      deletingUuid.value = null
    }
  }
  const editSnippet = (snippet: any) => {
    emit('edit', snippet)
  }
</script>

<template>
  <div class="flex flex-col">
    <!-- Header -->
    <div class="relative flex items-center border-b border-gray-100 px-4 py-3">
      <button
        type="button"
        class="shrink-0 text-gray-500 hover:text-gray-700"
        @click="emit('back')"
      >
        <ChevronLeft class="size-5" />
      </button>
      <div class="absolute left-1/2 flex -translate-x-1/2 items-center gap-2">
        <span class="text-sm font-semibold text-gray-800">{{ t('Snippets') }}</span>
        <span
          v-if="snippets.length"
          class="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-800"
        >
          {{ snippets.length }}
        </span>
      </div>
    </div>
    <p class="px-4 pb-1 pt-2 text-center text-xs text-gray-500">{{ t('Subtitle') }}</p>
    <!-- Snippets list -->
    <div class="max-h-[376px] overflow-y-auto px-4 py-2">
      <div
        v-if="snippetsLoading"
        class="flex items-center justify-center py-8"
      >
        <Loader2 class="size-5 animate-spin text-gray-400" />
      </div>
      <div
        v-else-if="snippets.length === 0"
        class="py-8 text-center text-sm text-gray-500"
      >
        {{ t('NoSnippets') }}
      </div>
      <div
        v-else
        class="space-y-2"
      >
        <div
          v-for="snippet in snippets"
          :key="snippet.uuid"
          class="group relative cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm
            hover:bg-gray-50"
          @click="useSnippet(snippet)"
        >
          <p class="text-sm font-medium text-gray-800">{{ snippet.title }}</p>
          <p class="mt-1 line-clamp-2 text-sm text-gray-600">{{ snippet.content }}</p>
          <!-- Edit / Delete actions -->
          <div
            class="absolute right-2 top-2 hidden items-center gap-1 group-hover:flex"
            @click.stop
          >
            <button
              type="button"
              class="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
              @click="editSnippet(snippet)"
            >
              <Pencil class="size-3.5" />
            </button>
            <button
              type="button"
              class="rounded p-1 text-gray-400 hover:bg-red-100 hover:text-red-600 disabled:opacity-50"
              :disabled="deletingUuid === snippet.uuid"
              @click="deleteSnippet(snippet)"
            >
              <Loader2
                v-if="deletingUuid === snippet.uuid"
                class="size-3.5 animate-spin"
              />
              <Trash2
                v-else
                class="size-3.5"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Generate document button (case only) -->
    <div
      v-if="caseData?.uuid"
      class="border-t border-gray-100 px-4 py-3"
    >
      <a
        :href="`${ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION.replace(':uuid', caseData.uuid)}`"
        class="flex w-full items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5
          text-sm font-medium text-gray-800 no-underline shadow-sm hover:bg-gray-50"
      >
        <FilePlus2 class="size-4 shrink-0" />
        {{ t('GenerateDocument') }}
      </a>
    </div>
  </div>
</template>
